/**
 * Timezone utility functions for IST (Indian Standard Time) handling
 * IST is UTC+5:30
 *
 * IMPORTANT: All dates stored in MongoDB are in UTC.
 * These utilities help convert UTC dates to IST for display.
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in milliseconds

/**
 * Get IST time components from a UTC date
 * @param utcDate - A UTC date (from MongoDB)
 * @returns IST time components
 */
export const getISTTimeComponents = (utcDate: Date | string) => {
  const date = new Date(utcDate);
  const istTime = new Date(date.getTime() + IST_OFFSET_MS);
  return {
    hours: istTime.getUTCHours(),
    minutes: istTime.getUTCMinutes(),
    seconds: istTime.getUTCSeconds(),
    date: istTime.getUTCDate(),
    month: istTime.getUTCMonth(),
    year: istTime.getUTCFullYear(),
    day: istTime.getUTCDay(),
  };
};

/**
 * Format a UTC date to IST time string (e.g., "09:15 AM")
 * @param utcDate - UTC date from MongoDB
 * @returns Formatted time string in IST
 */
export const formatTimeToIST = (utcDate: Date | string): string => {
  const { hours, minutes } = getISTTimeComponents(utcDate);
  const hours12 = hours % 12 || 12;
  const ampm = hours < 12 ? "AM" : "PM";
  const paddedMinutes = minutes.toString().padStart(2, "0");
  return `${hours12}:${paddedMinutes} ${ampm}`;
};

/**
 * Format a UTC date to IST date string (e.g., "25/12/2024")
 * @param utcDate - UTC date from MongoDB
 * @returns Formatted date string in IST
 */
export const formatDateToIST = (utcDate: Date | string): string => {
  const { date, month, year } = getISTTimeComponents(utcDate);
  const paddedDate = date.toString().padStart(2, "0");
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  return `${paddedDate}/${paddedMonth}/${year}`;
};

/**
 * Format a UTC date to IST datetime string (e.g., "25/12/2024 09:15 AM")
 * @param utcDate - UTC date from MongoDB
 * @returns Formatted datetime string in IST
 */
export const formatDateTimeToIST = (utcDate: Date | string): string => {
  return `${formatDateToIST(utcDate)} ${formatTimeToIST(utcDate)}`;
};

/**
 * Get start of day in IST as UTC (for queries)
 * @param utcDate - Any UTC date
 * @returns Start of that IST day as UTC Date
 */
export const getStartOfDayISTAsUTC = (utcDate: Date | string = new Date()): Date => {
  const date = new Date(utcDate);
  const istTime = new Date(date.getTime() + IST_OFFSET_MS);
  istTime.setUTCHours(0, 0, 0, 0);
  return new Date(istTime.getTime() - IST_OFFSET_MS);
};

/**
 * Get end of day in IST as UTC (for queries)
 * @param utcDate - Any UTC date
 * @returns End of that IST day as UTC Date
 */
export const getEndOfDayISTAsUTC = (utcDate: Date | string = new Date()): Date => {
  const date = new Date(utcDate);
  const istTime = new Date(date.getTime() + IST_OFFSET_MS);
  istTime.setUTCHours(23, 59, 59, 999);
  return new Date(istTime.getTime() - IST_OFFSET_MS);
};

/**
 * Convert IST date components to UTC Date for queries
 * @param year - Year
 * @param month - Month (0-indexed, 0 = January)
 * @param day - Day of month
 * @param hours - Hours in IST (default 0)
 * @param minutes - Minutes (default 0)
 * @returns UTC Date
 */
export const istToUTC = (
  year: number,
  month: number,
  day: number,
  hours: number = 0,
  minutes: number = 0
): Date => {
  const istDate = new Date(Date.UTC(year, month, day, hours, minutes, 0));
  return new Date(istDate.getTime() - IST_OFFSET_MS);
};

/**
 * Get IST day range as UTC dates for MongoDB queries
 * @param dateInput - Date input
 * @returns Object with startOfDay and endOfDay in UTC
 */
export const getISTDayRangeAsUTC = (dateInput: Date | string) => {
  return {
    startOfDay: getStartOfDayISTAsUTC(dateInput),
    endOfDay: getEndOfDayISTAsUTC(dateInput),
  };
};

/**
 * Check if a UTC date falls on today (IST)
 * @param utcDate - UTC date to check
 * @returns boolean
 */
export const isToday = (utcDate: Date | string): boolean => {
  const nowIST = getISTTimeComponents(new Date());
  const dateIST = getISTTimeComponents(utcDate);
  return (
    nowIST.date === dateIST.date &&
    nowIST.month === dateIST.month &&
    nowIST.year === dateIST.year
  );
};
